import React, { useState } from 'react';
import {
  Badge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { useTakeOverAvailable } from '../../hooks/useTakeOverAvailable';
import {
  externalCustodyTooltip,
  type TaskCustodyReason,
  type TaskCustodyValue,
} from './externalCustody';

export interface ExternalCustodyBadgeProps {
  custody: TaskCustodyValue | null | undefined;
  custodyReason?: TaskCustodyReason | null;
}

/**
 * The "External" pill on a task the External EMR still owns (Take Over v2).
 * Renders nothing for Roger's tasks, so call sites drop it in without a
 * conditional. A tap opens the explanation too, since phones have no hover.
 * A tap only opens it: Radix closes the tooltip on pointer-down, so a toggle
 * would close and reopen. An outside tap, Escape or blur closes it. The tap
 * stays on the pill instead of opening the task row.
 */
export const ExternalCustodyBadge: React.FC<ExternalCustodyBadgeProps> = ({
  custody,
  custodyReason,
}) => {
  const [open, setOpen] = useState(false);
  const takeOverAvailable = useTakeOverAvailable();
  if (custody !== 'external') return null;
  const explanation = externalCustodyTooltip(custodyReason, takeOverAvailable);

  return (
    <TooltipProvider>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <span
            role='button'
            tabIndex={0}
            aria-label={`External. ${explanation}`}
            className='inline-flex shrink-0 cursor-help'
            onClick={event => {
              event.stopPropagation();
              setOpen(true);
            }}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                setOpen(true);
              }
            }}
            data-testid='external-custody-badge'
          >
            <Badge variant='outline' className='whitespace-nowrap'>
              External
            </Badge>
          </span>
        </TooltipTrigger>
        <TooltipContent className='max-w-xs'>{explanation}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default ExternalCustodyBadge;
